import React from 'react';

import { useMutation } from '@apollo/client/react';
import { c } from 'ttag';

import { Alert, Button, Modal, ModalContent, ModalFooter } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { PortalUserDocument, ResendInvitationDocument } from '../../graphql/__generated__/types';
import { formatDate } from './display';
import { resendRefusalOf } from './resendRefusal';

interface Props {
    userId: string;
    email: string;
    /**
     * Whether a live activation link exists right now: `invitationExpiresAt` is filled for a
     * pending invitation only. It gates the revocation warnings, because `previousLinkRevoked`
     * comes back always true from the API even when nothing was pending (e.g. chained after a
     * reopening), and warning about a mail that was never sent misleads the operator.
     */
    hasLiveLink: boolean;
    onClose: () => void;
}

/**
 * The gesture rotates the token, so the link already in the person's mailbox stops working the
 * instant it runs. Both revocation sentences are gated on `hasLiveLink`.
 */
const ResendInvitationDialog = ({ userId, email, hasLiveLink, onClose }: Props) => {
    const [resend, { data, loading, error }] = useMutation(ResendInvitationDocument, {
        refetchQueries: [PortalUserDocument],
        awaitRefetchQueries: true,
    });

    const result = data?.resendInvitation;

    const submit = () => {
        resend({ variables: { userId } }).catch(() => {});
    };

    /* ------------------------------------------------------------------- done */
    if (result) {
        const expiry = result.expiresAt ? formatDate(result.expiresAt) : null;
        const sentCount = result.sentCount ?? undefined;

        return (
            <Modal open onClose={onClose} title={c('Resend invitation').t`Invitation sent again`}>
                <ModalContent>
                    <Alert
                        type={result.activationEmailSent ? 'success' : 'warning'}
                        title={
                            result.activationEmailSent
                                ? c('Resend invitation').t`A new link has left for ${email}.`
                                : c('Resend invitation').t`The new link is issued, but the email has not left yet.`
                        }
                    >
                        <p className="text-sm">
                            {!result.activationEmailSent
                                ? c('Resend invitation')
                                      .t`The send failed and sits in the retry queue. The new link exists and is valid — if the email never arrives, send it again from here.`
                                : hasLiveLink
                                  ? c('Resend invitation')
                                        .t`Tell them to use this new email: the link from the previous one no longer works.`
                                  : c('Resend invitation')
                                        .t`This is the only activation link they hold: nothing was pending before it.`}
                        </p>
                    </Alert>

                    <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {expiry && (
                            <div>
                                <dt className="text-xs font-semibold tracking-wide text-text-600 uppercase">
                                    {c('Resend invitation').t`New link valid until`}
                                </dt>
                                <dd className="mt-1 font-mono text-sm text-text-900">{expiry}</dd>
                            </div>
                        )}
                        {typeof sentCount === 'number' && (
                            <div>
                                <dt className="text-xs font-semibold tracking-wide text-text-600 uppercase">
                                    {c('Resend invitation').t`Invitations sent`}
                                </dt>
                                <dd className="mt-1 font-mono text-sm text-text-900">{sentCount}</dd>
                            </div>
                        )}
                    </dl>

                    {result.previousLinkRevoked && hasLiveLink && (
                        <p className="mt-4 text-xs text-text-600">
                            {c('Resend invitation')
                                .t`The previous link has been revoked. If they open it, they will be told it is not valid — that is expected, and the new email is the one to use.`}
                        </p>
                    )}
                </ModalContent>
                <ModalFooter>
                    <Button category="default" onClick={onClose}>
                        {c('Action').t`Close`}
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }

    /* ---------------------------------------------------------------- confirm */
    return (
        <Modal open onClose={onClose} title={c('Resend invitation').t`Send the invitation again`}>
            <ModalContent>
                <p className="text-xs text-text-600">
                    {c('Resend invitation')
                        .t`A new activation link will be issued and emailed to ${email}, with a fresh deadline.`}
                </p>

                {hasLiveLink && (
                    <Alert
                        type="warning"
                        className="mt-4"
                        title={c('Resend invitation').t`The previous link stops working`}
                    >
                        <p className="text-sm">
                            {c('Resend invitation')
                                .t`The link already in their mailbox is revoked at once — including if they are opening it right now. Make sure they know to use the new email.`}
                        </p>
                    </Alert>
                )}

                {error && (
                    <div className="mt-4">
                        <RefusalNotice refusal={resendRefusalOf(error)} />
                    </div>
                )}
            </ModalContent>
            <ModalFooter>
                <Button category="weak" onClick={onClose} disabled={loading}>
                    {c('Action').t`Cancel`}
                </Button>
                <Button category="default" onClick={submit} loading={loading}>
                    {c('Resend invitation').t`Send again`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

/** Used by the sheet to phrase the button when the invitation has already been chased. */
export const resendButtonLabel = (sentCount: number): string =>
    sentCount > 1
        ? // No `ngettext`: this branch only runs above one, so a singular form would
          // be a plural rule written for a case that cannot occur.
          c('Resend invitation').t`Send again (${sentCount} sends so far)`
        : c('Resend invitation').t`Send the invitation again`;

export default ResendInvitationDialog;
